import * as React from "react";
import { NumericFormat, type NumericFormatProps } from "react-number-format";

import { cn } from "@/lib/utils";

const TimeInput = React.forwardRef<
  HTMLInputElement,
  Omit<React.ComponentProps<"input">, "type">
>(({ className, ...props }, ref) => {
  return (
    <input
      type={"time"}
      className={cn(
        "h-10 border border-border rounded-md w-full px-3 py-2 text-sm placeholder:text-muted-foreground bg-background focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
        "focus:border-secondary transition-colors duration-200",
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});
TimeInput.displayName = "TimeInput";

function inputClassName(variant: "default" | "brand", className?: string) {
  return cn(
    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
    "focus:border-secondary transition-colors duration-200",
    variant === "default" && "file:text-foreground",
    variant === "brand" && "text-[#1f4746] file:text-[#1f4746]",
    className,
  );
}

type CurrencyInputProps = Omit<
  React.ComponentProps<"input">,
  "type" | "value" | "defaultValue" | "onChange"
> & {
  variant?: "default" | "brand";
  className?: string;
  valueCents?: number | null;
  onValueCentsChange?: (valueCents: number | null) => void;
  minCents?: number;
  maxCents?: number;
  prefix?: string;
};

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      variant = "brand",
      className,
      valueCents,
      onValueCentsChange,
      minCents,
      maxCents,
      prefix = "$",
      ...props
    },
    ref,
  ) => {
    const min = typeof minCents === "number" ? minCents / 100 : undefined;
    const max = typeof maxCents === "number" ? maxCents / 100 : undefined;

    return (
      <NumericFormat
        getInputRef={ref}
        className={inputClassName(variant, className)}
        value={typeof valueCents !== "number" ? "" : valueCents / 100}
        thousandSeparator
        decimalScale={2}
        allowNegative={false}
        inputMode="decimal"
        prefix={prefix}
        isAllowed={(vals) => {
          const n = vals.floatValue;

          if (typeof n !== "number") return true; // allow clearing
          if (typeof min === "number" && n < min) return false;
          if (typeof max === "number" && n > max) return false;

          return true;
        }}
        onValueChange={(vals) => {
          const dollars = vals.floatValue;
          const cents =
            typeof dollars !== "number" ? null : Math.round(dollars * 100);
          onValueCentsChange?.(cents);
        }}
        {...(props as NumericFormatProps)}
      />
    );
  },
);
CurrencyInput.displayName = "CurrencyInput";

const Input = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input"> & {
    variant?: "default" | "brand";
  }
>(({ className, type, variant = "brand", ...props }, ref) => {
  return (
    <>
      <input
        type={type}
        className={inputClassName(variant, className)}
        ref={ref}
        {...props}
      />
      {props.maxLength && typeof props.value === "string" && (
        <p className="text-right text-sm text-muted-foreground">
          {props.value.length}/{props.maxLength}
        </p>
      )}
    </>
  );
});
Input.displayName = "Input";

export { CurrencyInput, Input, TimeInput };
